// The diary's unit of display is a story, not an entry.
//
// Sam, 25 Aug: "when a new entry is added, if it's part of a trail of
// thought, we show it with the previous actions too... this might pull in
// multiple thoughts in a chain, from even days and hours ago... we nail
// this and present it well, then this will read as narrative in his day."
//
// So: an entry with causal ancestry renders as a group. Its ancestors
// stack above it, minimised (timestamp, link mark, one line), joined by a
// rail, and the newest entry sits full-size at the bottom as the thing
// that just happened. An ancestor consumed into a group does not also
// render at its own chronological position, because reading the same
// moment twice is the one thing worse than missing it.
//
// The feed stays newest-first at the STORY level: a story sits where its
// newest member sits. Chronology is bent, never broken: every timestamp
// is still on the page, and the bending is exactly the point, because
// "this, because of this, because of this" is an order of explanation,
// not an order of clock.
//
// Pure, no react, so the walk can be tested in node like any other logic.

// A cause recorded at schedule time and an entry written after a model
// call are the same event a few seconds apart, never to the millisecond.
const NEAR_MS = 120_000
// Six generations tells any story he has ever produced; past that it is
// likelier a cycle than a saga.
const MAX_DEPTH = 6

function nearest(list, ts) {
  let best = null
  for (const e of list) {
    const d = Math.abs(e.ts - ts)
    if (d >= NEAR_MS) continue
    if (!best || d < Math.abs(best.ts - ts)) best = e
  }
  return best
}

/**
 * The parent of an entry: the thing it happened because of.
 *
 * A recorded cause wins. The chain id is the fallback for entries written
 * before causes existed (and as a belt for chained reads): the nearest
 * OLDER entry wearing the same chain id.
 */
function parentOf(entry, all) {
  if (entry.cause?.ts) return nearest(all, entry.cause.ts)
  const id = entry.chain?.id
  if (!id) return null
  let best = null
  for (const e of all) {
    if (e === entry || e.chain?.id !== id) continue
    if (e.ts >= entry.ts) continue
    if (!best || e.ts > best.ts) best = e
  }
  return best
}

/**
 * Fold a newest-first entry list into stories.
 *
 * Returns rows in render order. Each row is either
 *   { entry }                                  an ordinary entry, or
 *   { entry, ancestors: [...] }                a story,
 * where ancestors run OLDEST FIRST (the order a reader falls through
 * them) and each is { entry } for one on the page or
 * { stub: { label, excerpt, ts } } for one that has scrolled beyond the
 * window: the thread does not stop existing because its start is old,
 * which is the "even days and hours ago" half of the ask.
 */
export function buildStories(shown) {
  const consumed = new Set()
  const rows = []

  for (const entry of shown) {
    if (consumed.has(entry)) continue

    const ancestors = []
    // The head claims itself before walking, or a cycle of causes could
    // pull it in as its own ancestor and render it twice in one group.
    consumed.add(entry)
    let walker = entry
    for (let depth = 0; depth < MAX_DEPTH; depth++) {
      const parent = parentOf(walker, shown)
      if (parent && !consumed.has(parent)) {
        consumed.add(parent)
        ancestors.push({ entry: parent })
        walker = parent
        continue
      }
      // No usable parent: either off the page, or already claimed by a
      // newer story. Both get a stub when the walker names a cause, so a
      // thread never silently truncates; it says where it went even when
      // where it went is rendered elsewhere.
      if (walker.cause?.label && (!parent || consumed.has(parent))) {
        ancestors.push({ stub: {
          label: walker.cause.label,
          excerpt: walker.cause.excerpt || null,
          ts: walker.cause.ts || null,
        } })
      }
      break
    }

    if (ancestors.length) {
      ancestors.reverse()
      rows.push({ entry, ancestors })
    } else {
      rows.push({ entry })
    }
  }

  return rows
}
